import type { MenuProps } from "antd";
import { Dropdown } from "antd";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { ChangePasswordModal } from "@/components/auth/ChangePasswordModal";
import { Avatar, I } from "@/components/design";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { useFirebaseAuth } from "@/hooks/useFirebaseAuth";
import { useProfile } from "@/hooks/useProfile";
import { handleSignOut } from "@/lib/auth-utils";
import { fontSize } from "@/theme/foundation";

export function UserMenu() {
  const { t } = useTranslation();
  const { user } = useFirebaseAuth();
  const { profile } = useProfile();
  const [changePasswordOpen, setChangePasswordOpen] = useState(false);

  // Only email/password users can change a password; Google logins have none.
  const isPasswordUser = user?.providerData.some((p) => p.providerId === "password") ?? false;

  const displayName =
    profile?.displayName ||
    [profile?.firstName, profile?.lastName].filter(Boolean).join(" ") ||
    user?.displayName ||
    user?.email ||
    t("common:userMenu.fallbackName");

  const email = profile?.email || user?.email || "";

  const items: MenuProps["items"] = [
    {
      key: "identity",
      label: (
        <div className="col gap-0.5" style={{ minWidth: 200, padding: "var(--space-1) 0" }}>
          <span style={{ fontWeight: 600, fontSize: fontSize.body, color: "var(--ink)" }}>{displayName}</span>
          {email && <span style={{ fontSize: fontSize.caption, color: "var(--ash)" }}>{email}</span>}
        </div>
      ),
      disabled: true,
    },
    { type: "divider" },
    {
      key: "language",
      // Not a navigation item — the switcher handles its own clicks, so the
      // row must not also close the menu underneath it.
      label: (
        // biome-ignore lint/a11y/noStaticElementInteractions: this wrapper only prevents the parent menu from consuming its child buttons' events.
        <div
          role="presentation"
          style={{ padding: "var(--space-0_5) 0" }}
          onClick={(e) => e.stopPropagation()}
          onKeyDown={(e) => e.stopPropagation()}
        >
          <LanguageSwitcher />
        </div>
      ),
    },
    { type: "divider" },
    ...(isPasswordUser
      ? [
          {
            key: "change-password",
            label: t("common:userMenu.changePassword"),
            onClick: () => setChangePasswordOpen(true),
          },
        ]
      : []),
    {
      key: "logout",
      label: <span style={{ color: "var(--error)" }}>{t("common:userMenu.signOut")}</span>,
      onClick: () => handleSignOut(),
    },
  ];

  return (
    <>
      <Dropdown menu={{ items }} trigger={["click"]} placement="topLeft">
        <button
          type="button"
          className="user-chip"
          aria-label={t("common:userMenu.accountOf", { name: displayName })}
          title={displayName}
        >
          <Avatar name={displayName} size={28} />
          <I.down size={12} stroke="var(--ash)" />
        </button>
      </Dropdown>
      <ChangePasswordModal open={changePasswordOpen} onClose={() => setChangePasswordOpen(false)} />
    </>
  );
}
